// SVG text has no layout engine, so charts reserve axis room from the widest formatted label
// instead of a fixed guess (a fixed 44px clipped "10,000,000.00").
const AVG_CHAR_EM = 0.58; // average glyph width of the dashboard font, in em (digits/commas)

export function textWidth(text: string, fontSize: number): number {
    return Math.ceil(text.length * fontSize * AVG_CHAR_EM);
}

/** Left padding for a y-axis: room for the widest tick label, never below `min`. */
export function axisPadLeft(labels: string[], fontSize: number, min: number): number {
    const widest = labels.reduce((w, l) => Math.max(w, textWidth(l, fontSize)), 0);
    return Math.max(min, widest + 10);
}

/** Right padding for value labels drawn after a bar, never below `min`. */
export function labelPadRight(labels: string[], fontSize: number, min: number): number {
    const widest = labels.reduce((w, l) => Math.max(w, textWidth(l, fontSize)), 0);
    return Math.max(min, widest + 10);
}

/** Compact money for axis ticks: 1,500,000 -> 1.5M, 750,000 -> 750K, 900 -> 900. Tooltips stay exact. */
export function compactMoney(n: number): string {
    const abs = Math.abs(n);
    const trim = (v: number) => String(Math.round(v * 10) / 10);
    if (abs >= 1e9) return `${trim(n / 1e9)}B`;
    if (abs >= 1e6) return `${trim(n / 1e6)}M`;
    if (abs >= 1e3) return `${trim(n / 1e3)}K`;
    return String(Math.round(n * 10) / 10);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 'YYYY-MM' -> 'Mar 2026' (or 'Mar 26' when `short`); anything else is returned unchanged. */
export function formatPeriodLabel(label: string, short = false): string {
    const m = /^(\d{4})-(\d{2})$/.exec(label);
    if (!m) return label;
    const idx = Number(m[2]) - 1;
    if (idx < 0 || idx > 11) return label;
    return `${MONTHS[idx]} ${short ? m[1].slice(2) : m[1]}`;
}

/** Show every Nth category label so the widest label never collides with its neighbour. */
export function labelEvery(labels: string[], step: number, fontSize: number): number {
    const widest = labels.reduce((w, l) => Math.max(w, textWidth(l, fontSize)), 0);
    return Math.max(1, Math.ceil((widest + 8) / Math.max(1, step)));
}
